import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import LandingPage from "@/pages/LandingPage";
import BrandSignup from "@/pages/BrandSignup";
import CreatorSignup from "@/pages/CreatorSignup";
import HomePage from "@/pages/HomePage";
import StreamerHome from "@/pages/StreamerHome";
import BrandHome from "@/pages/BrandHome";
import BrandDashboard from "@/pages/BrandDashboard";
import AdminPanel from "@/pages/AdminPanel";
import StreamerConsole from "@/pages/StreamerConsole";
import StreamerProfile from "@/pages/StreamerProfile";
import StreamerEarnings from "@/pages/StreamerEarnings";
import StreamerProfileView from "@/pages/StreamerProfileView";
import CreatorBids from "@/pages/CreatorBids";
import ProgrammaticBuilder from "@/pages/ProgrammaticBuilder";
import ObsOverlay from "@/pages/ObsOverlay";

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/landing" />;
  if (requiredRole && !user.roles?.includes(requiredRole)) return <Navigate to="/" />;
  return children;
}

function SmartHome() {
  const { user } = useAuth();
  // Streamer-only users get the dedicated streamer dashboard
  const isStreamerOnly = user?.roles?.includes('streamer') && !user?.roles?.includes('admin') && !user?.roles?.includes('brand');
  if (isStreamerOnly) return <StreamerHome />;
  // Brand-only users get the brand campaign HQ
  const isBrandOnly = user?.roles?.includes('brand') && !user?.roles?.includes('admin') && !user?.roles?.includes('streamer');
  if (isBrandOnly) return <BrandHome />;
  // Multi-role or admin users get the control center
  return <HomePage />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/landing" element={user ? <Navigate to="/" /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
      <Route path="/signup/brand" element={user ? <Navigate to="/" /> : <BrandSignup />} />
      <Route path="/signup/creator" element={user ? <Navigate to="/" /> : <CreatorSignup />} />
      <Route path="/" element={<ProtectedRoute><SmartHome /></ProtectedRoute>} />
      <Route path="/brand" element={<ProtectedRoute requiredRole="brand"><BrandDashboard /></ProtectedRoute>} />
      <Route path="/brand/programmatic" element={<ProtectedRoute requiredRole="brand"><ProgrammaticBuilder /></ProtectedRoute>} />
      <Route path="/marketplace/streamer/:profileId" element={<ProtectedRoute><StreamerProfileView /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminPanel /></ProtectedRoute>} />
      <Route path="/streamer" element={<ProtectedRoute requiredRole="streamer"><StreamerConsole /></ProtectedRoute>} />
      <Route path="/streamer/profile" element={<ProtectedRoute requiredRole="streamer"><StreamerProfile /></ProtectedRoute>} />
      <Route path="/streamer/earnings" element={<ProtectedRoute requiredRole="streamer"><StreamerEarnings /></ProtectedRoute>} />
      <Route path="/streamer/bids" element={<ProtectedRoute requiredRole="streamer"><CreatorBids /></ProtectedRoute>} />
      <Route path="/obs-overlay" element={<ObsOverlay />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: '#121212', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' } }} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
